import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { eventService, Event, Comment } from '../services/eventService';
import { useAuth } from '../context/AuthContext';

export const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    if (id) {
      loadEvent();
      eventService.incrementView(id).catch(() => {
        // Erreur silencieuse
      });
    }
  }, [id]);

  const loadEvent = async () => {
    if (!id) return;
    try {
      const data = await eventService.getById(id);
      setEvent(data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user || !id) return;
    try {
      await eventService.like(id);
      loadEvent();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !id || !comment.trim()) return;
    
    setSubmitting(true);
    try {
      await eventService.comment(id, comment);
      setComment('');
      setTimeout(() => {
        loadEvent();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'ajout du commentaire. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.contenu);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEdit = async (commentId: number) => {
    if (!editingCommentText.trim()) return;
    
    setSubmitting(true);
    try {
      await eventService.updateComment(commentId, editingCommentText);
      setEditingCommentId(null);
      setEditingCommentText('');
      setTimeout(() => {
        loadEvent();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification du commentaire. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) return;
    
    setDeletingCommentId(commentId);
    try {
      await eventService.deleteComment(commentId);
      setTimeout(() => {
        loadEvent();
      }, 500);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du commentaire. Veuillez réessayer.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const getTypeInfo = (type: string) => {
    const types: { [key: string]: { text: string; icon: string; color: string } } = {
      balayage: { text: 'Balayage', icon: '🧹', color: 'from-blue-500 to-blue-600' },
      reunion: { text: 'Réunion', icon: '🤝', color: 'from-green-500 to-green-600' },
      dahira: { text: 'Événement Dahira', icon: '📅', color: 'from-purple-500 to-purple-600' },
      autre: { text: 'Autre', icon: '📌', color: 'from-gray-500 to-gray-600' },
    };
    return types[type] || { text: type, icon: '📅', color: 'from-blue-500 to-blue-600' };
  };

  const getStatusLabel = (statusAuto?: string) => {
    const currentStatus = statusAuto || 'a_venir';
    const labels: { [key: string]: { text: string; icon: string; color: string } } = {
      a_venir: { text: 'À venir', icon: '⏳', color: 'bg-blue-100 text-blue-800' },
      en_cours: { text: 'En cours', icon: '🟢', color: 'bg-green-100 text-green-800' },
      termine: { text: 'Terminé', icon: '✅', color: 'bg-gray-200 text-gray-900 border border-gray-300' },
      passe: { text: 'Passé', icon: '📜', color: 'bg-gray-200 text-gray-900 border border-gray-300' },
    };
    return labels[currentStatus] || labels['a_venir'];
  };

  // Créer un tableau de photos (pour l'instant une seule, mais peut être étendu)
  const photos = event?.photo ? [event.photo] : [];

  const nextPhoto = () => {
    if (photos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    }
  };

  const prevPhoto = () => {
    if (photos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    }
  };

  useEffect(() => {
    // Auto-slide toutes les 5 secondes si plus d'une photo
    if (photos.length > 1) {
      const interval = setInterval(() => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [photos.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="relative">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-700"></div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-8xl mb-6">📅</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Événement non trouvé</h2>
          <Link to="/events" className="text-blue-700 hover:text-blue-800 font-semibold">
            ← Retour aux événements
          </Link>
        </div>
      </div>
    );
  }

  const typeInfo = getTypeInfo(event.type_event);

  return (
    <div className="min-h-screen">
      {/* Hero Section avec Slide Photo */}
      <section className="relative bg-gradient-to-br from-blue-50 via-green-50/30 to-blue-100/50 py-12 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Bouton retour */}
          <button
            onClick={() => navigate('/events')}
            className="mb-6 inline-flex items-center space-x-2 text-gray-700 hover:text-blue-700 transition-colors font-medium"
          >
            <span>←</span>
            <span>Retour aux événements</span>
          </button>

          {/* Card principale */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-blue-100/50 overflow-hidden">
            {/* Header avec Slide Photo */}
            <div className="relative h-96 sm:h-[500px] bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
              {photos.length > 0 ? (
                <div className="relative w-full h-full">
                  {/* Slide Photo */}
                  <div className="relative w-full h-full overflow-hidden">
                    {photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`${event.titre} - Photo ${index + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                          index === currentPhotoIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Navigation flèches - seulement si plus d'une photo */}
                  {photos.length > 1 && (
                    <>
                      <button
                        onClick={prevPhoto}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
                        aria-label="Photo précédente"
                      >
                        <span className="text-2xl">←</span>
                      </button>
                      <button
                        onClick={nextPhoto}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm"
                        aria-label="Photo suivante"
                      >
                        <span className="text-2xl">→</span>
                      </button>
                    </>
                  )}

                  {/* Dots indicateurs - seulement si plus d'une photo */}
                  {photos.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
                      {photos.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPhotoIndex(index)}
                          className={`h-3 rounded-full transition-all duration-300 ${
                            index === currentPhotoIndex
                              ? 'bg-white w-8 shadow-lg'
                              : 'bg-white/50 w-3 hover:bg-white/75'
                          }`}
                          aria-label={`Aller à la photo ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>
                  
                  {/* Infos sur la photo */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white pointer-events-none z-10">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-4xl">{typeInfo.icon}</span>
                      <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                        {typeInfo.text}
                      </span>
                      {(() => {
                        const statusLabel = getStatusLabel(event.status_auto);
                        return (
                          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusLabel.color}`}>
                            {statusLabel.icon} {statusLabel.text}
                          </span>
                        );
                      })()}
                      {photos.length > 1 && (
                        <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                          📸 {currentPhotoIndex + 1} / {photos.length}
                        </span>
                      )}
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-bold mb-4">{event.titre}</h1>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <span className="text-8xl opacity-50 mb-4 block">{typeInfo.icon}</span>
                    <span className="text-2xl font-bold">{event.titre}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Contenu */}
            <div className="p-8">
              {/* Infos événement */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="text-sm text-gray-600 mb-1">📅 Date</div>
                  <div className="font-bold text-gray-900">{new Date(event.date_event).toLocaleDateString('fr-FR')}</div>
                  <div className="text-sm text-gray-600">🕐 {event.heure_debut}</div>
                  {event.heure_fin && (
                    <div className="text-xs text-gray-500">jusqu'à {event.heure_fin}</div>
                  )}
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="text-sm text-gray-600 mb-1">📍 Lieu</div>
                  <div className="font-bold text-gray-900">{event.lieu}</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="text-sm text-gray-600 mb-1">📌 Type</div>
                  <div className="font-bold text-gray-900">{typeInfo.text}</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="text-sm text-gray-600 mb-1">📊 Statistiques</div>
                  <div className="flex items-center space-x-3 text-sm">
                    <span>👁️ {event.nombre_vues || 0}</span>
                    <span>❤️ {event.nombre_likes || 0}</span>
                    <span>💬 {event.nombre_commentaires || 0}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed whitespace-pre-line break-words overflow-wrap-anywhere">{event.description}</p>
              </div>

              {/* Actions */}
              {user && (
                <div className="mb-8">
                  <button
                    onClick={handleLike}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                      event.is_liked
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {event.is_liked ? '❤️ Liké' : '🤍 Liker'}
                  </button>
                </div>
              )}

              {/* Galerie Photos (si plusieurs photos) */}
              {photos.length > 1 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">📸 Galerie Photos</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {photos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPhotoIndex(index)}
                        className={`relative aspect-square rounded-xl overflow-hidden border-4 transition-all duration-300 transform hover:scale-105 ${
                          index === currentPhotoIndex
                            ? 'border-blue-600 shadow-xl ring-4 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-400'
                        }`}
                      >
                        <img
                          src={photo}
                          alt={`${event.titre} - Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {index === currentPhotoIndex && (
                          <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                            <span className="text-white text-2xl">✓</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section Commentaires */}
      <section className="relative bg-gradient-to-br from-blue-50/30 via-white to-green-50/50 py-10 sm:py-14 md:py-16 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-blue-200/15 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-blue-100/50 p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              💬 Commentaires ({event.nombre_commentaires || 0})
            </h2>

            {user ? (
              <form onSubmit={handleComment} className="mb-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  required
                  className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white mb-4 min-h-[100px]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {submitting ? 'Envoi...' : 'Commenter'}
                </button>
              </form>
            ) : (
              <div className="mb-8 p-6 bg-yellow-50 rounded-xl border border-yellow-200">
                <p className="text-gray-700">
                  <Link to="/login" className="text-blue-700 hover:text-blue-800 font-semibold underline">
                    Connectez-vous
                  </Link>
                  {' '}pour commenter
                </p>
              </div>
            )}

            <div className="space-y-4">
              {event.commentaires_approuves && event.commentaires_approuves.length > 0 ? (
                event.commentaires_approuves.map((comment: Comment) => {
                  const isOwner = user && comment.utilisateur.id === user.id;
                  const isEditing = editingCommentId === comment.id;
                  
                  return (
                    <div
                      key={comment.id}
                      className="bg-gray-50 rounded-xl p-6 border border-gray-200 animate-slide-up"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                            {(comment.utilisateur.first_name || comment.utilisateur.username || 'U')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 truncate">
                              {comment.utilisateur.first_name || comment.utilisateur.username}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {new Date(comment.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                        {isOwner && !isEditing && (
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => handleEditComment(comment)}
                              className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
                              disabled={submitting || deletingCommentId !== null}
                            >
                              <span className="hidden sm:inline">✏️ Modifier</span>
                              <span className="sm:hidden">✏️</span>
                            </button>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-red-600 hover:text-red-800 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-lg hover:bg-red-50 transition-all whitespace-nowrap"
                              disabled={submitting || deletingCommentId === comment.id}
                            >
                              {deletingCommentId === comment.id ? (
                                <span className="text-xs">Suppression...</span>
                              ) : (
                                <>
                                  <span className="hidden sm:inline">🗑️ Supprimer</span>
                                  <span className="sm:hidden">🗑️</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            value={editingCommentText}
                            onChange={(e) => setEditingCommentText(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white min-h-[100px]"
                            placeholder="Modifier votre commentaire..."
                          />
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2">
                            <button
                              onClick={() => handleSaveEdit(comment.id)}
                              disabled={submitting || !editingCommentText.trim()}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                            >
                              {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={submitting}
                              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-700 leading-relaxed">{comment.contenu}</p>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-6xl mb-4">💬</div>
                  <p className="text-gray-600 text-lg">Aucun commentaire pour le moment</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

